(function () {
  'use strict';

  // This asset can be rendered by more than one product form. Keep only one
  // global cart controller so the same click/submit is never handled twice.
  if (window.__DTC_AJAX_CART_CONTROLLER__) return;
  window.__DTC_AJAX_CART_CONTROLLER__ = true;

  class AjaxAddToCart {
    constructor() {
      this.bindEvents();
    }

    bindEvents() {
      // Capture the submit before delegated theme handlers. This prevents one
      // Add to Cart click from being submitted by two AJAX systems at once.
      document.addEventListener('submit', (event) => {
        const form = event.target;
        if (!form || !form.matches || !form.matches('[data-type="add-to-cart-form"]')) return;

        event.preventDefault();
        event.stopImmediatePropagation();
        this.handleFormSubmit(form);
      }, true);

      // Sticky Add to Cart: handle it once and stop other delegated handlers
      // from issuing a second /cart/add request.
      document.addEventListener('click', (event) => {
        const button = event.target.closest && event.target.closest('.t4s-sticky-atc__atc[data-action-atc]');
        if (!button) return;

        event.preventDefault();
        event.stopImmediatePropagation();
        this.handleStickyAddToCart(button);
      }, true);

      // Always refresh Mini Cart before opening it from a cart trigger.
      document.addEventListener('click', async (event) => {
        const cartTrigger = event.target.closest && event.target.closest('[data-drawer-options]');
        const drawerOptions = cartTrigger && cartTrigger.getAttribute('data-drawer-options');
        if (!drawerOptions || drawerOptions.indexOf('#t4s-mini_cart') === -1) return;

        event.preventDefault();
        event.stopImmediatePropagation();

        await this.updateCartCount();
        await this.updateCartContentWithRetry();
        this.openCartDrawer();
      }, true);

      // Browser back/forward cache can restore stale cart markup.
      window.addEventListener('pageshow', async () => {
        await this.updateCartCount();
        await this.updateCartContentWithRetry();
      });

      // Trash icon: make this controller the single owner of the remove action.
      document.addEventListener('click', (event) => {
        const removeButton = event.target.closest && event.target.closest('[data-cart-remove]');
        if (!removeButton) return;

        event.preventDefault();
        event.stopImmediatePropagation();
        this.handleRemoveItem(removeButton);
      }, true);
    }

    getRootUrl() {
      const shopifyRoot = window.Shopify && window.Shopify.routes && window.Shopify.routes.root;
      if (shopifyRoot) return shopifyRoot.endsWith('/') ? shopifyRoot : shopifyRoot + '/';
      return '/';
    }

    async handleFormSubmit(form) {
      const submitButton = form.querySelector('[data-atc-form]');
      if (!submitButton || submitButton.disabled || submitButton.getAttribute('aria-disabled') === 'true') return;

      const originalButtonText = submitButton.innerHTML;
      let addSucceeded = false;

      try {
        submitButton.disabled = true;
        submitButton.innerHTML = '<span>Adding...</span>';

        // Send the original FormData so variant id, quantity, line-item
        // properties, selling plan and any Shopify-supported product fields are
        // preserved instead of rebuilding only part of the form by hand.
        const formData = new FormData(form);
        const response = await fetch(this.getRootUrl() + 'cart/add.js', {
          method: 'POST',
          headers: { 'Accept': 'application/json' },
          body: formData
        });

        const payload = await this.safeJson(response);
        if (!response.ok) {
          const message = payload && (payload.description || payload.message || payload.status);
          throw new Error(message || 'Failed to add item to cart');
        }

        addSucceeded = true;

        // Once /cart/add succeeded, UI refresh problems must not be presented
        // as an Add to Cart failure. Refresh the count and drawer separately.
        await this.updateCartCount();
        const refreshed = await this.updateCartContentWithRetry();
        if (!refreshed) {
          console.warn('Item was added, but Mini Cart refresh did not complete.');
        }

        this.openCartDrawer();
        submitButton.innerHTML = '<span>Added!</span>';
      } catch (error) {
        console.error('Error adding to cart:', error);
        // Only show Error when the actual /cart/add request failed.
        submitButton.innerHTML = addSucceeded ? '<span>Added!</span>' : '<span>Error</span>';
      } finally {
        setTimeout(() => {
          submitButton.innerHTML = originalButtonText;
          submitButton.disabled = false;
        }, 1600);
      }
    }

    async handleStickyAddToCart(button) {
      if (!button || button.disabled || button.getAttribute('aria-disabled') === 'true') return;

      const variantId = button.getAttribute('data-variant-id');
      const buttonTextElement = button.querySelector('.t4s-btn-atc_text');
      if (!variantId || !buttonTextElement) return;

      const originalButtonText = buttonTextElement.innerHTML;
      let addSucceeded = false;

      try {
        button.disabled = true;
        buttonTextElement.innerHTML = 'Adding...';

        const response = await fetch(this.getRootUrl() + 'cart/add.js', {
          method: 'POST',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ id: variantId, quantity: 1 })
        });

        const payload = await this.safeJson(response);
        if (!response.ok) {
          const message = payload && (payload.description || payload.message || payload.status);
          throw new Error(message || 'Failed to add item to cart');
        }

        addSucceeded = true;
        await this.updateCartCount();
        const refreshed = await this.updateCartContentWithRetry();
        if (!refreshed) {
          console.warn('Sticky Add to Cart succeeded, but Mini Cart refresh did not complete.');
        }

        this.openCartDrawer();
        buttonTextElement.innerHTML = 'Added!';
      } catch (error) {
        console.error('Error adding to cart:', error);
        buttonTextElement.innerHTML = addSucceeded ? 'Added!' : 'Error';
      } finally {
        setTimeout(() => {
          buttonTextElement.innerHTML = originalButtonText;
          button.disabled = false;
        }, 1600);
      }
    }

    async handleRemoveItem(button) {
      const itemKey = button.getAttribute('data-id');
      if (!itemKey) return;

      const cartItem = button.closest('[data-cart-item]');
      if (cartItem) {
        cartItem.style.opacity = '0.5';
        cartItem.style.pointerEvents = 'none';
        cartItem.setAttribute('aria-busy', 'true');
      }

      let removeSucceeded = false;
      let cart = null;

      try {
        const response = await fetch(this.getRootUrl() + 'cart/change.js', {
          method: 'POST',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ id: itemKey, quantity: 0 })
        });

        const payload = await this.safeJson(response);
        if (!response.ok) {
          const message = payload && (payload.description || payload.message || payload.status);
          throw new Error(message || 'Failed to remove item from cart');
        }

        removeSucceeded = true;
        cart = payload && typeof payload.item_count === 'number' ? payload : await this.updateCartCount();

        // /cart/change succeeded, so immediately refresh Mini Cart markup.
        // This was missing in the previous version and caused the dimmed old
        // cart item to remain frozen until a page refresh/close-reopen.
        await this.updateCartCount(cart);
        const refreshed = await this.updateCartContentWithRetry();

        if (!refreshed && cartItem && cartItem.isConnected) {
          // Last-resort visual fallback: the server cart is already correct, so
          // never leave an unclickable stale row on screen.
          cartItem.remove();
        }
      } catch (error) {
        console.error('Error removing item from cart:', error);
      } finally {
        if (!removeSucceeded && cartItem && cartItem.isConnected) {
          cartItem.style.opacity = '1';
          cartItem.style.pointerEvents = 'auto';
          cartItem.removeAttribute('aria-busy');
        }
      }
    }

    async safeJson(response) {
      try {
        return await response.json();
      } catch (error) {
        return null;
      }
    }

    async updateCartCount(existingCart) {
      try {
        let cart = existingCart;
        if (!cart || typeof cart.item_count !== 'number') {
          const response = await fetch(this.getRootUrl() + 'cart.js', {
            headers: { 'Accept': 'application/json' },
            cache: 'no-store'
          });
          if (!response.ok) throw new Error('Failed to fetch cart count');
          cart = await response.json();
        }

        const cartCountElements = document.querySelectorAll('[data-cart-count]');
        cartCountElements.forEach(element => {
          element.textContent = cart.item_count;
          element.classList.toggle('t4s-op-0', cart.item_count <= 0);
        });

        const cartWrappers = document.querySelectorAll('[data-cart-wrapper]');
        cartWrappers.forEach(wrapper => {
          wrapper.setAttribute('data-ccount', cart.item_count);
        });

        this.syncCartCountClass(cart.item_count);
        return cart;
      } catch (error) {
        console.error('Error updating cart count:', error);
        return null;
      }
    }

    syncCartCountClass(count) {
      const html = document.documentElement;
      const stale = [];
      html.classList.forEach(cls => {
        if (cls.indexOf('t4s-cart-count-') === 0) stale.push(cls);
      });
      stale.forEach(cls => html.classList.remove(cls));
      html.classList.add('t4s-cart-count-' + count);
    }

    async updateCartContentWithRetry() {
      if (await this.updateCartContent()) return true;
      await new Promise(resolve => setTimeout(resolve, 180));
      return this.updateCartContent();
    }

    async updateCartContent() {
      try {
        const separator = this.getRootUrl().indexOf('?') > -1 ? '&' : '?';
        const url = this.getRootUrl() + separator + 'section_id=mini_cart&_=' + Date.now();
        const response = await fetch(url, {
          headers: { 'Accept': 'text/html' },
          cache: 'no-store'
        });

        if (!response.ok) throw new Error('Mini Cart section request failed: ' + response.status);

        const html = await response.text();
        if (!html || /Liquid error/i.test(html)) {
          throw new Error('Mini Cart section returned a Liquid error');
        }

        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const freshCart = doc.querySelector('#t4s-mini_cart');
        const currentCart = document.querySelector('#t4s-mini_cart');

        if (!freshCart || !currentCart) {
          throw new Error('Mini Cart markup not found in section response');
        }

        this.syncInnerHtml(freshCart, currentCart, '[data-cart-items]');
        this.syncInnerHtml(freshCart, currentCart, '[data-cart-prices]');
        this.syncInnerHtml(freshCart, currentCart, '[data-cart-discounts]');

        // Shipping threshold sits outside [data-cart-items], so update it too.
        const freshShipping = freshCart.querySelector('[data-cart-calc-shipping]');
        const currentShipping = currentCart.querySelector('[data-cart-calc-shipping]');
        if (freshShipping && currentShipping) {
          currentShipping.innerHTML = freshShipping.innerHTML;
          const percent = freshShipping.getAttribute('data-t4s-percent');
          if (percent !== null) currentShipping.setAttribute('data-t4s-percent', percent);
        }

        // Keep the cart wrapper count in sync with the freshly rendered section.
        const freshCount = freshCart.getAttribute('data-ccount');
        if (freshCount !== null) currentCart.setAttribute('data-ccount', freshCount);

        return true;
      } catch (error) {
        console.error('Error updating cart content:', error);
        return false;
      }
    }

    syncInnerHtml(sourceRoot, targetRoot, selector) {
      const source = sourceRoot.querySelector(selector);
      const target = targetRoot.querySelector(selector);
      if (source && target) {
        target.innerHTML = source.innerHTML;
      }
    }

    openCartDrawer() {
      const miniCart = document.getElementById('t4s-mini_cart');
      if (!miniCart) {
        console.error('Mini cart drawer not found');
        return;
      }

      const overlay = document.querySelector('.t4s-close-overlay');
      if (overlay) overlay.classList.add('is--visible');

      miniCart.classList.add('is--visible');
      miniCart.setAttribute('aria-hidden', 'false');
      document.body.classList.add('t4s-drawer-open');

      // Avoid adding another identical close listener every time the drawer opens.
      if (!miniCart.dataset.dtcCloseBound) {
        miniCart.dataset.dtcCloseBound = 'true';
        miniCart.addEventListener('click', (event) => {
          if (event.target.closest && event.target.closest('[data-drawer-close]')) {
            this.closeCartDrawer();
          }
        });
      }

      if (overlay && !overlay.dataset.dtcCloseBound) {
        overlay.dataset.dtcCloseBound = 'true';
        overlay.addEventListener('click', () => this.closeCartDrawer());
      }
    }

    closeCartDrawer() {
      const miniCart = document.getElementById('t4s-mini_cart');
      const overlay = document.querySelector('.t4s-close-overlay');

      if (miniCart) {
        miniCart.classList.remove('is--visible');
        miniCart.setAttribute('aria-hidden', 'true');
      }

      if (overlay) overlay.classList.remove('is--visible');
      document.body.classList.remove('t4s-drawer-open');
    }
  }

  new AjaxAddToCart();
})();
